import './Devices.css';
import AssignedDevice from './AssignedDevice/AssignedDevice';
import UnassignedDevice from './UnassignedDevices/UnassignedDevices';
import FlushAccordion from 'src/SharedComponents/FlushAccordion/FlushAccordion';
import { IDevice, IPartner } from 'src/Utils/Hooks/useGetAdminInfo/useGetAuraSupportInfo';
import { useAuraSupportInfo } from 'src/Utils/Hooks/UseAuraInfoContext';
import SearchBar from 'src/SharedComponents/SearchBar/SearchBar';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearch } from 'src/Utils/Hooks/SearchData/useSearch';
import { useGetAuraSupportInfoWrapper } from 'src/Utils/Hooks/useGetAdminInfo/useGetAuraSupportInfoWrapper';
import AssignDeviceToPartner from './AssignDeviceToPartner/AssignDeviceToPartner';
import SelectedItemsBanner from 'src/SharedComponents/SelectedItemsBanner/SelectedItemsBanner';
import { useGetDeviceServiceWrapper } from 'src/Utils/Hooks/DeviceService/useGetDeviceServiceWrapper';

const Devices: React.FC = () => {
    const { getAuraSupportInfo } = useGetAuraSupportInfoWrapper();
    const { adminDetails } = useAuraSupportInfo();
    const partnerDetails: IPartner[] | undefined = adminDetails?.partners;
    const [selectedDevices, setSelectedDevices] = useState<IDevice[]>([]);
    const [searchText, setSearchText] = useState<string>('');
    const [showAssignPartner, setShowAssignPartner] = useState<boolean>(false);

    const { reqestResponse, assignDeviceToPartner } = useGetDeviceServiceWrapper();

    const clearSelectedDevices = useCallback(() => {
        setSelectedDevices([]);
    }, [setSelectedDevices]);

    useEffect(() => {
        if(reqestResponse){
            getAuraSupportInfo();
            clearSelectedDevices();
        }
    }, [reqestResponse, getAuraSupportInfo, clearSelectedDevices]);
    
    const updateDeviceCheckbox = (device: IDevice, isSelected: boolean) => {
        setSelectedDevices((prevSelectedDevices) => {
            if (isSelected) {
                return [...prevSelectedDevices, device]; // Add device to the list when checked
            } else {
                return prevSelectedDevices.filter((d: IDevice) => d.deviceId !== device.deviceId); // Remove device when unchecked
            }
        });
    }

    const searchKeys = useRef<(keyof IDevice)[]>(['serialNumber', 'distributorName', 'clinicName'])
    const filteredDevices = useSearch(adminDetails?.devices || [], searchText, searchKeys.current);
    const getTotalDevicesCountByAssignment = (assignedToDistributor: boolean) => {
        // Total devices by assignment status (assigned or unassigned)
        const devices = adminDetails?.devices.filter((device: IDevice) =>
            device.isAssignedToDistributor === assignedToDistributor
        ) ?? [];
        return devices.length;
    };
    
    const getDevicesByAssignment = (assignedToDistributor: boolean) => {
        // Filter devices by assignment status (assigned or unassigned)
        const devices = filteredDevices?.filter((device: IDevice) =>
            device.isAssignedToDistributor === assignedToDistributor
        ) ?? [];

        // Separate selected and unselected devices
        const selectedDevicesList = devices.filter((device) =>
            selectedDevices.some((selected) => selected.deviceId === device.deviceId)
        );
    
        const unselectedDevicesList = devices.filter((device) =>
            !selectedDevices.some((selected) => selected.deviceId === device.deviceId)
        );
    
        // Sort the unselected devices by serial number
        const sortedUnselectedDevices = unselectedDevicesList.sort((a: IDevice, b: IDevice) => {
            if (a.serialNumber === null || a.serialNumber === undefined) return 1;
            if (b.serialNumber === null || b.serialNumber === undefined) return -1;
            return a.serialNumber.localeCompare(b.serialNumber);
        });

        // Combine selected devices (maintaining the order of selection) and sorted unselected devices
        return [...selectedDevicesList, ...sortedUnselectedDevices];
    };

    return (
        <>
            {
                showAssignPartner && 
                    <AssignDeviceToPartner
                        onClose={() => {setShowAssignPartner(false)}}
                        onSubmit={(distributorId: string, distributorName: string) => {
                            if(selectedDevices.length === 1){
                                assignDeviceToPartner(selectedDevices.map(device => device.deviceId), distributorId, distributorName, selectedDevices.map(device => device.serialNumber)[0]);
                            }
                            else{
                                assignDeviceToPartner(selectedDevices.map(device => device.deviceId), distributorId, distributorName);
                            }
                            setShowAssignPartner(false)
                        }}
                        partners={partnerDetails}
                        selectedDevices={selectedDevices}
                    />
            }
            <div>
                <div className="row m-2" style={{width:"97%"}}>
                    <SearchBar onChange={(text:string) => {setSearchText(text) }} />
                </div>
            </div>
            {adminDetails && 
                <div>
                    <FlushAccordion title={`${'Assigned Devices'} (${getDevicesByAssignment(true)?.length ?? 0} / ${getTotalDevicesCountByAssignment(true) ?? 0})`} id="assignedDevices">
                        <>
                            <div className="m-3">
                                {getDevicesByAssignment(true)?.map((device:IDevice, index:number) => (
                                    <div key={index}>
                                        <AssignedDevice device={device} updateDevices={getAuraSupportInfo}/>
                                    </div>
                                ))}
                            </div>
                        </>
                    </FlushAccordion>
                    <hr className='line mt-4'/>
                    <FlushAccordion title={`${'Devices on Stock'} (${getDevicesByAssignment(false)?.length ?? 0} / ${getTotalDevicesCountByAssignment(false) ?? 0})`} id="unassignedDevices">
                        <>
                            <div className="m-3" style={{paddingBottom: '100px'}}>
                                {getDevicesByAssignment(false)?.map((device:IDevice, index:number) => (
                                    <div key={index}>
                                        <UnassignedDevice 
                                            device={device} 
                                            partner={partnerDetails} 
                                            updateDevices={getAuraSupportInfo} 
                                            updateDeviceCheckbox={updateDeviceCheckbox}
                                            isChecked={selectedDevices.some(d => d.deviceId === device.deviceId)}
                                            updateSelectedDevices={clearSelectedDevices}
                                        />
                                    </div>
                                ))}
                            </div>
                        </>
                    </FlushAccordion>
                    {
                        selectedDevices.length > 0 &&
                        <SelectedItemsBanner
                            selectedItemType="devices"
                            selectedItemsCount={selectedDevices.length}
                            clearSelectedItems={clearSelectedDevices}
                            setShowAssignPartner={setShowAssignPartner}
                        />
                    }
                </div>
            }
        </>
    )
}

export default Devices;